import { useMemo } from 'react';
import { Col, Modal, Row } from 'antd';
import DataGrid, {
  Column,
  Export,
  Grouping,
  Pager,
  Sorting,
  HeaderFilter,
  ColumnFixing,
  FilterRow,
  Paging,
} from 'devextreme-react/data-grid';
import { NumericFormat } from 'react-number-format';
import CustomStore from 'devextreme/data/custom_store';

import { ContractAPI } from '../../../Api/ContractAPI';
import { SignatureAPI } from '../../../Api/SignatureAPI';
import { RepresentativesAPI } from '../../../Api/RepresentativesAPI';
import { BeneficiaryAPI } from '../../../Api/BeneficiaryAPI';
import { InvestorAPI } from '../../../Api/InvestorAPI';

import { useTableAllContracts } from '../../../Hooks/useTableAllContracts/useTableAllContracts';

import { ITableAllContracts } from './ITableAllContracts';

const TableAllContracts = ({
	baseURL,
	baseURLExternal,
	translations,
	userIdInstitution,
	selectedInstitutionId,
	selectedVehicleId,
	clientId,
	language,
	moduleRole
}: ITableAllContracts) => {
	const contractAPI = useMemo(() => ContractAPI(baseURL, baseURLExternal, clientId), [baseURL, baseURLExternal, clientId]);
	const signaturitAPI = useMemo(() => SignatureAPI(baseURL, clientId), [baseURL, clientId]);
	const representativesAPI = useMemo(() => RepresentativesAPI(baseURL, clientId), [baseURL, clientId]);
	const beneficiaryAPI = useMemo(() => BeneficiaryAPI(baseURL, clientId), [baseURL, clientId]);
	const investorsAPI = useMemo(() => InvestorAPI(baseURL, baseURLExternal, clientId), [baseURL, baseURLExternal, clientId]);
	
	const {
		dataSource,
		gridKey,
		isAfinRejectedModalOpen,
		afinRejectedMessage,
		cellRenderEditIcon,
		cellRenderCancelIcon,
		onOpenAfinRejectedModal,
		closeAfinRejectedModal
	} = useTableAllContracts({
		contractAPI,
		signaturitAPI,
		representativesAPI,
		investorsAPI,
		beneficiaryAPI,
		userIdInstitution,
		selectedInstitutionId,
		selectedVehicleId,
		translations,
		moduleRole
	});

	const buildRemoteHeaderFilterDataSource = (field: string) => (options: any) => {
		options.dataSource = {
			store: new CustomStore({
				load: async () => {
					const response = await contractAPI.getAllContractsHeaderFilterValues({
						field,
						institutionId: selectedInstitutionId || undefined,
						vehicleId: selectedVehicleId ?? undefined
					});

					const items = response?.success ? response.data ?? [] : [];
					return items.map((item: any) => {
						const value = item.value ?? item.Value;
						return {
							text: item.text ?? item.Text ?? String(value ?? ''),
							value,
						};
					});
				}
			}),
			paginate: false,
		};
	};


	return (
		<Row className="table-all-contracts" justify="center">
			<Col span={24}>
						<DataGrid
							key={gridKey}
							id="General"
							showBorders={true}
							height={'75vh'}
							remoteOperations={true}
							allowColumnResizing={true}
							columnResizingMode={"widget"}
							columnMinWidth={60}
							rowAlternationEnabled={true}
							showRowLines={true}
							hoverStateEnabled={true}
							dataSource={dataSource}
							onEditorPreparing={(e: any) => {
								if (e.parentType === 'headerRow' && e.editorName === 'dxSelectBox') {
									e.editorOptions.showClearButton = true;
								}
							}}
						>
							<HeaderFilter visible={true} />
							<Export enabled={true} />
							<Sorting mode="multiple" />
							<ColumnFixing enabled={true} />
							<FilterRow visible={true} />
							<Grouping contextMenuEnabled={true} expandMode="rowClick" />
							<Grouping autoExpandAll={false} />
							<Paging defaultPageSize={50} />
							<Pager
								showPageSizeSelector={true}
								allowedPageSizes={[20, 50, 100]}
								showNavigationButtons={true}
								visible={true}
								showInfo={true}
							/>
							<Column dataField="id" caption="Contract ID" width={60} headerFilter={{ dataSource: buildRemoteHeaderFilterDataSource("id") }} />
							<Column dataField="investorName" caption={translations.nombre} minWidth={220} alignment='center' headerFilter={{ dataSource: buildRemoteHeaderFilterDataSource("investorName") }}/>
							<Column dataField="personType" caption={translations.type} alignment='center' width={100} headerFilter={{ dataSource: buildRemoteHeaderFilterDataSource("personType") }}/>
							<Column dataField="vehicle" caption={translations.vehiculo} alignment='center' minWidth={220} headerFilter={{ dataSource: buildRemoteHeaderFilterDataSource("vehicle") }}/>
							<Column dataField="commitment" caption={translations.compromiso} width={120} alignment='right' 
								headerFilter={{ dataSource: buildRemoteHeaderFilterDataSource("commitment") }}
								cellRender={(row) =>                  
									<NumericFormat
										thousandSeparator={language === 'es' ? "." : ","}
										decimalSeparator={language === 'es' ? "," : "."}
										value={row.data.commitment}
										displayType={"text"}
										prefix={"€"}
									/>
								} 
							/>
							<Column dataField="state" caption={translations.estado} alignment='center' width={120}
								headerFilter={{ dataSource: buildRemoteHeaderFilterDataSource("state") }}
								cellRender={(row) => {
									const state = row.data.state;
									return (
										<span onClick={() => state === 'Afin Rejected' ? onOpenAfinRejectedModal(row.data) : {}}>
											{translations[state] || state}
										</span>
									);
								}}
							/>
							<Column dataField="created_At" caption={translations.fecha} width={110} dataType="date" format="dd/MM/yyyy" alignment='center' headerFilter={{ dataSource: buildRemoteHeaderFilterDataSource("created_At") }}/>
							<Column dataField="placementAgentName" caption={translations.distribuidor} visible={userIdInstitution === 536} minWidth={150} alignment='center' headerFilter={{ dataSource: buildRemoteHeaderFilterDataSource("placementAgentName") }}/>
							<Column caption={moduleRole === "Read" ? "View" : "Edit"} cellRender={cellRenderEditIcon} width={60} alignment='center' allowHeaderFiltering={false} />
							<Column caption="Cancel/Delete" cellRender={cellRenderCancelIcon} width={60} alignment='center' allowEditing={false} allowHeaderFiltering={false} />
						</DataGrid>
				</Col>
			<Modal
				title="AFIN rejected contract reasons"
				open={isAfinRejectedModalOpen}
				onCancel={closeAfinRejectedModal}
				footer={null}
			>
				{afinRejectedMessage}
			</Modal>
		</Row>
	);
};

export default TableAllContracts;
